import { useEffect, useRef, useState } from "react"
import { ID, Query, type Models } from "appwrite"
import { databases } from "@/lib/appwrite"
import { toast } from "react-toastify"
import { useAuth } from "@/context/AuthContext"
import { useNavigate } from "@tanstack/react-router"
import type { Instrument } from "@/Wist-blockchain/collections/instruments/types"
import type { Company } from "@/lib/collections/organizations/getOrganizationsFetch"
import type { ReportActivitiesForm } from "@/Wist-blockchain/collections/reportActivities/types"
import type { ReportMaterialsForm } from "@/Wist-blockchain/collections/reportMaterials/types"
import { type FinalStatus, type ServiceReport, type ServiceReportForm, type ServiceType, serializeAttachment, parseAttachment } from "@/Wist-blockchain/collections/serviceReport/types"
import { createActivity, type ActivityRow } from "@/Wist-blockchain/serviceReport/components/activities-table"
import { createMaterial, type MaterialRow } from "@/Wist-blockchain/serviceReport/components/materials-table"
import type { PendingFile } from "@/Wist-blockchain/serviceReport/components/anexos-files-uploader"
import { generateReportCode, incrementReportSequence } from "@/Wist-blockchain/serviceReport/utils/generateReportCode"
import { uploadReportFile } from "@/Wist-blockchain/serviceReport/utils/uploadAnexosStorage"
import { finalSchema, type FinalSchemaErrors } from "./serviceReportSchema"
import { getReportsByInstrument } from "@/Wist-blockchain/collections/serviceReport/getAllServiceReport"
import { updateInstrument } from "@/Wist-blockchain/collections/instruments/instruments.service"


const DB = import.meta.env.VITE_BLOCKCHAIN_DATABASE_ID
const COL_REPORT = import.meta.env.VITE_BLOCKCHAIN_SERVICE_REPORT_COLLECTION_ID
const COL_ACTIVITIES = import.meta.env.VITE_BLOCKCHAIN_REPORT_ACTIVITIES_COLLECTION_ID
const COL_MATERIALS = import.meta.env.VITE_BLOCKCHAIN_REPORT_MATERIALS_COLLECTION_ID

export interface RevisionMode {
    originalIdReport: string
    nextRevisionNumber: number
}

// helpers 
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const toISO = (date: string, time?: string): string | null => {
    if (!date?.trim() || !DATE_RE.test(date)) return null
    if (time !== undefined && !time.trim()) return null
    if (time) {
        // Soporta "HH:MM" y "HH:MM:SS" (step="1" en el input devuelve segundos)
        const hhmmss = time.length >= 8 ? time.slice(0, 8) : `${time.slice(0, 5)}:00`
        return `${date}T${hhmmss}.000+00:00`
    }
    return `${date}T00:00:00.000+00:00`
}
const toNullable = (val: string): string | null => val.trim() || null
const reorder = <T extends { order: number }>(items: T[]): T[] =>
    items.map((item, i) => ({ ...item, order: i + 1 }))

// ─── Build payload ────────────────────────────────────────────────────────────
const buildPayload = (form: ServiceReportForm, status: "draft" | "final") => ({
    ...(toISO(form.serviceDate) && { serviceDate: toISO(form.serviceDate) }),
    ...(toISO(form.reportDate) && { reportDate: toISO(form.reportDate) }),
    ...(toISO(form.serviceDate, form.entryTime) && { serviceStartTime: toISO(form.serviceDate, form.entryTime) }),
    ...(toISO(form.serviceDate, form.exitTime) && { serviceEndTime: toISO(form.serviceDate, form.exitTime) }),
    ...(toISO(form.nextMaintenanceDate) && { nextMaintenanceData: toISO(form.nextMaintenanceDate) }),
    serviceType: (form.serviceTypes[0] ?? null) as ServiceType | null,
    serviceTypes: form.serviceTypes,
    purchaseOrder: toNullable(form.purchaseOrder),
    engineerId: form.engineerId || null,
    clientContact: toNullable(form.clientContact),
    clientArea: toNullable(form.clientArea),
    clientPosition: toNullable(form.clientPosition),
    clientPhone: toNullable(form.clientPhone),
    clientEmail: toNullable(form.clientEmail),
    qualificationType: form.qualificationTypes[0] ?? null,
    qualificationTypes: form.qualificationTypes,
    ...(toISO(form.nextQualificationDate) && { nextQualificationDate: toISO(form.nextQualificationDate) }),
    idInstrument: form.idInstrument || null,
    serviceReason: toNullable(form.serviceReason),
    reasonDescription: toNullable(form.reasonDescription),
    finalStatus: toNullable(form.finalStatus),
    verifiedParameters: form.verifiedParameters,
    recommendations: form.recommendations,
    requiresRemoteFollow: form.requiresRemoteFollow,
    clientResponsible: toNullable(form.clientResponsible),
    clientResponsiblePosition: toNullable(form.clientResponsiblePosition),
    deliveredOperative: form.deliveredOperative,
    clientObservations: form.clientObservations ? [form.clientObservations] : [],
    idAttachments: form.idAttachments,
    documentReferences: form.documentReferences,
    status,
    parentReportId: form.parentReportId ?? null,
    revisionNumber: form.revisionNumber != null ? String(form.revisionNumber) : null,
    internalNotes: toNullable(form.internalNotes),
})

// ─── Save children (borrar viejos + crear nuevos) ─────────────────────────────
const saveChildren = async (
    idReport: string,
    activities: ActivityRow[],
    materials: MaterialRow[],
) => {
    const [acts, mats] = await Promise.all([
        databases.listDocuments(DB, COL_ACTIVITIES, [Query.equal("idReport", idReport), Query.limit(100)]),
        databases.listDocuments(DB, COL_MATERIALS, [Query.equal("idReport", idReport), Query.limit(100)]),
    ])
    await Promise.all([
        ...acts.documents.map(d => databases.deleteDocument(DB, COL_ACTIVITIES, d.$id)),
        ...mats.documents.map(d => databases.deleteDocument(DB, COL_MATERIALS, d.$id)),
    ])
    await Promise.all([
        ...activities.filter(a => a.activity.trim()).map(a =>
            databases.createDocument(DB, COL_ACTIVITIES, ID.unique(), {
                idReport, order: a.order,
                activity: a.activity || null,
                result: a.result || null,
                observations: a.observations || null,
            })
        ),
        ...materials.filter(m => m.name.trim()).map(m =>
            databases.createDocument(DB, COL_MATERIALS, ID.unique(), {
                idReport, order: m.order,
                name: m.name || null, code: m.code || null,
                quantity: m.quantity, condition: m.condition, unitCost: m.unitCost, ivaPercent: m.ivaPercent,
            })
        ),
    ])
}

// Initial state
const INITIAL_REPORT: ServiceReportForm = {
    reportCode: "", serviceDate: "", reportDate: "", entryTime: "", exitTime: "",
    serviceTypes: [] as ServiceType[], purchaseOrder: "", engineerId: "",
    clientName: "", clientRuc: "", clientAddress: "", clientContact: "", clientArea: "",
    clientCity: "", qualificationTypes: [] as string[], nextQualificationDate: "", clientPhone: "", clientEmail: "",
    clientPosition: "", clientComercialName: "", idInstrument: "", equipmentSnapshot: "",
    serviceReason: "", reasonDescription: "", finalStatus: "" as FinalStatus | "",
    verifiedParameters: [], recommendations: [], nextMaintenanceDate: "",
    requiresRemoteFollow: false, clientResponsible: "", clientResponsiblePosition: "", deliveredOperative: false,
    clientObservations: "", idAttachments: [], documentReferences: [],
    status: null, parentReportId: null, revisionNumber: null, internalNotes: "",
}

//  Map ServiceReport  ServiceReportForm 
const mapReportToForm = (source: ServiceReport): ServiceReportForm => ({
    ...INITIAL_REPORT,
    reportCode: source.idReport ?? "",
    serviceDate: source.serviceDate?.split("T")[0] ?? "",
    reportDate: source.reportDate?.split("T")[0] ?? "",
    entryTime: source.serviceStartTime?.split("T")[1]?.slice(0, 8) ?? "",
    exitTime: source.serviceEndTime?.split("T")[1]?.slice(0, 8) ?? "",
    serviceTypes: source.serviceTypes as ServiceType[] ?? (source.serviceType ? [source.serviceType] : []),
    purchaseOrder: source.purchaseOrder ?? "",
    engineerId: source.engineerId ?? "",
    clientContact: source.clientContact ?? "",
    clientArea: source.clientArea ?? "",
    clientPosition: source.clientPosition ?? "",
    clientPhone: source.clientPhone ?? "",
    clientEmail: source.clientEmail ?? "",
    qualificationTypes: source.qualificationTypes ?? (source.qualificationType ? [source.qualificationType] : []),
    nextQualificationDate: source.nextQualificationDate?.split("T")[0] ?? "",
    serviceReason: source.serviceReason ?? "",
    reasonDescription: source.reasonDescription ?? "",
    finalStatus: source.finalStatus ?? "",
    verifiedParameters: source.verifiedParameters ?? [],
    recommendations: source.recommendations ?? [],
    nextMaintenanceDate: source.nextMaintenanceData?.split("T")[0] ?? "",
    requiresRemoteFollow: source.requiresRemoteFollow ?? false,
    clientResponsible: source.clientResponsible ?? "",
    clientResponsiblePosition: source.clientResponsiblePosition ?? "",
    deliveredOperative: source.deliveredOperative ?? false,
    clientObservations: source.clientObservations?.[0] ?? "",
    idAttachments: source.idAttachments ?? [],
    documentReferences: source.documentReferences ?? [],
    idInstrument: source.idInstrument ?? "",
    status: "draft",
    parentReportId: source.parentReportId ?? null,
    // revisionNumber en Appwrite es string → lo convertimos a número para el form
    revisionNumber: source.revisionNumber ? Number(source.revisionNumber) : null,
    // Campos de empresa: siempre vacíos desde el draft (vienen de la organización)
    clientName: "",
    clientRuc: "",
    clientAddress: "",
    clientCity: "",
    clientComercialName: "",
    internalNotes: source.internalNotes ?? "",
})

// hook 
export const useServiceReportForm = (
    instrument?: Instrument,
    company?: Company,
    draftId?: string,
    initialDraft?: ServiceReport,
    initialActivities?: Models.Document[],
    initialMaterials?: Models.Document[],
    revisionMode?: RevisionMode,
) => {
    const { user } = useAuth()
    const navigate = useNavigate()

    // Si hay draftId usamos el idReport del borrador; si es nuevo (revisión o informe) generamos uno temporal
    const docIdRef = useRef<string | null>(draftId ?? null)
    const idReportRef = useRef<string>(draftId ? (initialDraft?.idReport ?? ID.unique()) : ID.unique())

    const [report, setReport] = useState<ServiceReportForm>(() => {
        if (!initialDraft) return INITIAL_REPORT

        const base = mapReportToForm(initialDraft)

        if (revisionMode) {
            return {
                ...base,
                reportCode: `${revisionMode.originalIdReport}-REV${revisionMode.nextRevisionNumber}`,
                parentReportId: revisionMode.originalIdReport,
                revisionNumber: revisionMode.nextRevisionNumber,
                // En revisión nueva: solo limpiar la fecha del informe (reportDate).
                // La fecha de servicio se arrastra del original como referencia.
                ...(!draftId && {
                    reportDate: "",
                }),
            }
        }

        return base
    })

    const [activities, setActivities] = useState<ActivityRow[]>(() => {
        if (!initialActivities?.length) return [createActivity(1)]
        return initialActivities.map(a => ({
            id: a.$id, order: a.order, idReport: a.idReport,
            activity: a.activity ?? "", result: a.result ?? "", observations: a.observations ?? "",
        }))
    })

    const [materials, setMaterials] = useState<MaterialRow[]>(() => {
        if (!initialMaterials?.length) return [createMaterial(1)]
        return initialMaterials.map(m => ({
            id: m.$id, order: m.order, idReport: m.idReport,
            name: m.name ?? "", code: m.code ?? "",
            quantity: m.quantity ?? 1, condition: m.condition ?? "Nuevo", unitCost: m.unitCost ?? 0, ivaPercent: m.ivaPercent ?? 15,
        }))
    })

    const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])

    const removeExistingAttachment = (index: number) => {
        setReport(prev => ({
            ...prev,
            idAttachments: prev.idAttachments.filter((_, i) => i !== index),
        }))
        markDirty()
    }

    const updateExistingDescription = (index: number, desc: string) => {
        setReport(prev => {
            const updated = [...prev.idAttachments]
            const current = parseAttachment(updated[index] ?? "")
            updated[index] = serializeAttachment(current.id, desc)
            return { ...prev, idAttachments: updated }
        })
        markDirty()
    }

    const [engineerLabel, setEngineerLabel] = useState("")
    const [saving, setSaving] = useState(false)
    const [savingDraft, setSavingDraft] = useState(false)
    const [formErrors, setFormErrors] = useState<FinalSchemaErrors>({})
    const [previousServiceInfo, setPreviousServiceInfo] = useState<{ isFirst: boolean; lastDate: string | null }>({ isFirst: true, lastDate: null })
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

    const markDirty = () => setHasUnsavedChanges(true)

    const setField = <K extends keyof ServiceReportForm>(key: K, value: ServiceReportForm[K]) => {
        setReport(prev => ({ ...prev, [key]: value }))
        markDirty()
    }

    const addActivity = () => { setActivities(prev => [...prev, createActivity(prev.length + 1)]); markDirty() }
    const removeActivity = (id: string) => { setActivities(prev => reorder(prev.filter(a => a.id !== id))); markDirty() }
    const updateActivity = (id: string, key: keyof ReportActivitiesForm, value: string) => {
        setActivities(prev => prev.map(a => a.id === id ? { ...a, [key]: value } : a))
        markDirty()
    }
    const addMaterial = () => { setMaterials(prev => [...prev, createMaterial(prev.length + 1)]); markDirty() }
    const removeMaterial = (id: string) => { setMaterials(prev => reorder(prev.filter(m => m.id !== id))); markDirty() }
    const updateMaterial = (id: string, key: keyof ReportMaterialsForm, value: string | number) => {
        setMaterials(prev => prev.map(m => m.id === id ? { ...m, [key]: value } : m))
        markDirty()
    }
    const totalMaterials = materials.reduce((sum, m) => sum + m.quantity * m.unitCost, 0)

    useEffect(() => {
        if (instrument?.$id && !initialDraft) setReport(prev => ({ ...prev, idInstrument: instrument.$id }))
    }, [instrument?.$id])

    useEffect(() => {
        if (!company) return
        setReport(prev => ({
            ...prev,
            clientComercialName: company.commercialName,
            clientRuc: company.ruc,
            clientAddress: company.address,
            clientCity: company.city,
            clientName: company.name,
        }))
    }, [company])

    useEffect(() => {
        if (!user?.$id) return
        const idToUse = initialDraft?.engineerId ?? user.$id
        generateReportCode(idToUse).then(({ engineerLabel }) => {
            setEngineerLabel(engineerLabel)
            if (!initialDraft) setReport(prev => ({ ...prev, engineerId: user.$id }))
        })
    }, [user?.$id])

    useEffect(() => {
        if (!instrument?.$id) return
        getReportsByInstrument(instrument.$id)
            .then(reports => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const finals = reports.filter((r: any) => r.status === "final")
                if (finals.length === 0) {
                    setPreviousServiceInfo({ isFirst: true, lastDate: null })
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const sorted = [...finals].sort((a: any, b: any) =>
                        (b.serviceDate ?? "").localeCompare(a.serviceDate ?? "")
                    )
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setPreviousServiceInfo({ isFirst: false, lastDate: (sorted[0] as any).serviceDate ?? null })
                }
            })
            .catch(() => setPreviousServiceInfo({ isFirst: true, lastDate: null }))
    }, [instrument?.$id])

    //  GUARDAR BORRADOR 
    const handleSaveDraft = async () => {
        setSavingDraft(true)
        try {
            const idReport = idReportRef.current

            let newSerializedAttachments: string[] = []
            if (pendingFiles.length > 0) {
                const results = await Promise.allSettled(pendingFiles.map(pf => uploadReportFile(pf.file)))
                const failed: string[] = []
                const successfulLocalIds: string[] = []
                results.forEach((r, i) => {
                    if (r.status === "fulfilled") {
                        newSerializedAttachments.push(serializeAttachment(r.value, pendingFiles[i].description))
                        successfulLocalIds.push(pendingFiles[i].localId)
                    } else {
                        console.error(`[Upload error] ${pendingFiles[i].name}:`, r.reason)
                        failed.push(pendingFiles[i].name)
                    }
                })
                if (failed.length) toast.warning(`No se pudieron subir: ${failed.join(", ")}. El resto se guardó correctamente.`)
                setPendingFiles(prev => prev.filter(pf => !successfulLocalIds.includes(pf.localId)))
                setReport(prev => ({
                    ...prev,
                    idAttachments: [...(prev.idAttachments ?? []), ...newSerializedAttachments],
                }))
            }

            const mergedReport = newSerializedAttachments.length > 0
                ? { ...report, idAttachments: [...(report.idAttachments ?? []), ...newSerializedAttachments] }
                : report

            const payload = { idReport, ...buildPayload(mergedReport, "draft") }

            if (docIdRef.current) {
                await databases.updateDocument(DB, COL_REPORT, docIdRef.current, payload)
                await saveChildren(idReport, activities, materials)
                toast.success("Borrador actualizado.")
                setHasUnsavedChanges(false)
            } else {
                const doc = await databases.createDocument(DB, COL_REPORT, ID.unique(), payload)
                docIdRef.current = doc.$id
                await saveChildren(idReport, activities, materials)
                toast.success("Borrador guardado.")
                setHasUnsavedChanges(false)
                // Navegar a la ruta de borrador (funciona para regulares y de revisión)
                navigate({
                    to: '/instruments/serviceReport/$id/$draftId',
                    params: {
                        id: report.idInstrument,
                        draftId: doc.$id,
                    },
                })
            }
        } catch (err) {
            console.error(err)
            toast.error("Error al guardar borrador.")
        } finally {
            setSavingDraft(false)
        }
    }

    //  GUARDAR FINAL 
    const handleSubmit = async () => {
        const validation = finalSchema.safeParse(report)
        if (!validation.success) {
            const fieldErrors: FinalSchemaErrors = {}
            for (const issue of validation.error.issues) {
                const field = issue.path[0] as keyof FinalSchemaErrors
                if (field && !fieldErrors[field]) fieldErrors[field] = issue.message
            }
            setFormErrors(fieldErrors)
            toast.error("Completa los campos obligatorios antes de guardar.")
            return
        }
        setFormErrors({})
        if (!user?.$id) return

        const today = new Date().toISOString().split("T")[0]

        setSaving(true)
        try {
            // Subir archivos (igual para informe normal y revisión)
            let newSerializedAttachments: string[] = []
            if (pendingFiles.length > 0) {
                const results = await Promise.allSettled(pendingFiles.map(pf => uploadReportFile(pf.file)))
                const failed: string[] = []
                results.forEach((r, i) => {
                    if (r.status === "fulfilled") {
                        newSerializedAttachments.push(serializeAttachment(r.value, pendingFiles[i].description))
                    } else {
                        console.error(`[Upload error] ${pendingFiles[i].name}:`, r.reason)
                        failed.push(pendingFiles[i].name)
                    }
                })
                if (failed.length) toast.warning(`No se pudieron subir: ${failed.join(", ")}. El resto se guardó correctamente.`)
            }

            const mergedAttachments = [...(report.idAttachments ?? []), ...newSerializedAttachments]

            if (revisionMode) {
                //  Revision: codigo derivado, no se incrementa secuencia
                const revisionCode = `${revisionMode.originalIdReport}-REV${revisionMode.nextRevisionNumber}`
                const finalPayload = {
                    idReport: revisionCode,
                    ...buildPayload({ ...report, idAttachments: mergedAttachments, reportDate: today }, "final"),
                    engineerId: user.$id,
                }

                if (docIdRef.current) {
                    await databases.updateDocument(DB, COL_REPORT, docIdRef.current, finalPayload)
                    await saveChildren(idReportRef.current, [], [])
                    await saveChildren(revisionCode, activities, materials)
                } else {
                    await databases.createDocument(DB, COL_REPORT, ID.unique(), finalPayload)
                    await saveChildren(revisionCode, activities, materials)
                }

                if (report.finalStatus) {
                    await updateInstrument(report.idInstrument, { status: report.finalStatus })
                }
                setHasUnsavedChanges(false)
                toast.success(`Revisión ${revisionCode} guardada.`)
                navigate({ to: '/instruments/$id', params: { id: report.idInstrument } })
            } else {
                //  Informe nuevo: genera código secuencial
                const { code } = await generateReportCode(user.$id)
                await incrementReportSequence()

                const finalPayload = {
                    idReport: code,
                    ...buildPayload({ ...report, idAttachments: mergedAttachments, reportDate: today }, "final"),
                    engineerId: user.$id,
                }

                if (docIdRef.current) {
                    await databases.updateDocument(DB, COL_REPORT, docIdRef.current, finalPayload)
                    await saveChildren(idReportRef.current, [], [])
                    await saveChildren(code, activities, materials)
                } else {
                    await databases.createDocument(DB, COL_REPORT, ID.unique(), finalPayload)
                    await saveChildren(code, activities, materials)
                }

                if (report.finalStatus) {
                    await updateInstrument(report.idInstrument, { status: report.finalStatus })
                }
                setHasUnsavedChanges(false)
                toast.success("Informe guardado correctamente.")
                navigate({ to: '/instruments/$id', params: { id: report.idInstrument } })
            }
        } catch (err) {
            console.error(err)
            toast.error("Error al guardar el informe.")
        } finally {
            setSaving(false)
        }
    }

    return {
        report, setField,
        activities, addActivity, removeActivity, updateActivity,
        materials, addMaterial, removeMaterial, updateMaterial, totalMaterials,
        pendingFiles, setPendingFiles,
        removeExistingAttachment, updateExistingDescription,
        engineerLabel, saving, savingDraft,
        handleSubmit, handleSaveDraft,
        formErrors,
        previousServiceInfo,
        hasUnsavedChanges,
    }
}
